import React, { Component } from 'react';

/* ── Inline SVG icons for each step ── */
const StepIcon = ({ name, size = 16, ...props }) => {
  const icons = {
    SummarizeAndExtractStep: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    AnalyzePlotStep: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    ),
    StructuralAndThematicStep: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
    PlanRewriteStep: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
  };
  return icons[name] || null;
};

const RestartIcon = ({ size = 14, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const CheckIcon = ({ size = 14, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LockIcon = ({ size = 13, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const STEP_FIG_COST = {
  SummarizeAndExtractStep: '1 fig/chapter',
  AnalyzePlotStep: '2 figs',
  StructuralAndThematicStep: '2 figs',
  PlanRewriteStep: '3 figs',
  DetectShowVsTellStep: '1 fig',
  DetectPacingIssuesStep: '1 fig',
  ChapterHooksAndScenesStep: '1 fig',
  DetectChapterHolesStep: '1 fig',
};

/**
 * AgentStepsChips — pure UI step chips
 *
 * Renders a vertical column of chips, one per agent step.
 * Visual state (completed, active, disabled) is derived from props.
 *
 * Props:
 *  - steps                : [{ name, title, description, enabled }]
 *  - currentStep          : string | number | null
 *  - currentlyExecutingStep : string | null
 *  - isRunning            : boolean
 *  - isPaused             : boolean
 *  - onRerunFromStep      : (stepName) => void
 *  - stepResults          : [{ step, success }]
 *  - onViewResults        : (stepName) => void         (optional)
 *  - viewableSteps        : Set<string>                (optional)
 */
export default class AgentStepsChips extends Component {
  state = {
    hoveredStep: null,
  };

  handleMouseEnter = (stepName) => {
    this.setState({ hoveredStep: stepName });
  };

  handleMouseLeave = () => {
    this.setState({ hoveredStep: null });
  };

  render() {
    const {
      steps, currentStep, currentlyExecutingStep,
      isRunning, isPaused, onRerunFromStep,
      stepResults, onViewResults, viewableSteps,
    } = this.props;
    const { hoveredStep } = this.state;

    // Build completed set from stepResults + currentStep index + chapter metadata
    const completedStepNames = new Set();
    const blockedStepNames = new Set();

    const failedStepNames = new Set();

    if (stepResults && stepResults.length > 0) {
      stepResults.forEach(r => {
        if (r.success) completedStepNames.add(r.step);
        else if (r.metadata?.blocked) blockedStepNames.add(r.step);
        else failedStepNames.add(r.step);
      });
    }

    if (currentStep != null && steps && steps.length > 0) {
      const currentStepIdx = typeof currentStep === 'number'
        ? currentStep
        : steps.findIndex(s => s.name === currentStep);
      if (currentStepIdx > 0) {
        for (let i = 0; i < currentStepIdx; i++) {
          completedStepNames.add(steps[i].name);
        }
      }
    }

    // Enable icon if metadata exists for the step
    const metadata = this.props.metadata;
    if (metadata) {
      steps.forEach(step => {
        switch (step.name) {
          case 'DetectShowVsTellStep':
            if (metadata.showVsTell) completedStepNames.add(step.name);
            break;
          case 'DetectPacingIssuesStep':
            if (metadata.pacing) completedStepNames.add(step.name);
            break;
          case 'ChapterHooksAndScenesStep':
            if (metadata.hooks || metadata.scenes) completedStepNames.add(step.name);
            break;
          case 'DetectChapterHolesStep':
            if (metadata.holes) completedStepNames.add(step.name);
            break;
          default:
            break;
        }
      });
    }

    return (
      <div className="agent-steps">
        {steps.map((step, idx) => {
          const isCurrentlyExecuting = isRunning && currentlyExecutingStep === step.name;
          const isCompleted = completedStepNames.has(step.name);
          const isFailed = failedStepNames.has(step.name);
          const isBlocked = blockedStepNames.has(step.name);
          const canRerun = !isRunning && !isPaused && step.enabled;
          const canViewResults = isCompleted && onViewResults && viewableSteps?.has(step.name);
          const hasIcon = !!StepIcon({ name: step.name });

          return (
            <div
              key={step.name}
              className={`agent-chip${isCurrentlyExecuting ? ' active pulsating' : ''}${isCompleted ? ' completed' : ''}${isFailed ? ' failed' : ''}${isBlocked ? ' blocked' : ''}${!step.enabled ? ' disabled' : ''}`}
              onMouseEnter={() => this.handleMouseEnter(step.name)}
              onMouseLeave={this.handleMouseLeave}
            >
              {/* Step icon */}
              <span className={`chip-step-icon${isCompleted ? ' chip-step-icon--completed' : ''}${isFailed ? ' chip-step-icon--failed' : ''}${isCurrentlyExecuting ? ' chip-step-icon--active' : ''}`}>
                {isCompleted
                  ? <CheckIcon size={14} />
                  : isFailed
                    ? <span style={{ fontSize: '14px', lineHeight: 1 }}>✗</span>
                    : isBlocked
                      ? <LockIcon size={14} />
                      : hasIcon
                        ? <StepIcon name={step.name} size={15} />
                        : <span className="chip-step-number">{idx + 1}</span>
                }
              </span>

              {/* Clickable step name */}
              <span
                className={`chip-step-name${canViewResults ? ' chip-step-name--clickable' : ''}`}
                onClick={canViewResults ? (e) => { e.stopPropagation(); onViewResults(step.name); } : undefined}
                title={canViewResults ? `View ${step.title} results` : step.title}
                role={canViewResults ? 'button' : undefined}
                tabIndex={canViewResults ? 0 : undefined}
              >
                {step.title}
              </span>

              {!step.enabled && <span className="dev-badge">Soon</span>}

              {/* Spacer pushes right-side actions to the end */}
              <span className="chip-spacer" />

              {/* Fig cost badge */}
              {step.enabled && STEP_FIG_COST[step.name] && (
                <span className="chip-coin-cost" title="Fig cost for this step">
                  ◎ {STEP_FIG_COST[step.name]}
                </span>
              )}

              {/* Restart icon */}
              {canRerun && onRerunFromStep && (
                <span
                  className="chip-restart-btn"
                  onClick={(e) => { e.stopPropagation(); onRerunFromStep(step.name); }}
                  title={`Rerun from ${step.title}`}
                  role="button"
                  tabIndex={0}
                >
                  <RestartIcon size={13} />
                </span>
              )}

              {/* Tooltip on hover */}
              {hoveredStep === step.name && step.description && (
                <div className="step-tooltip">
                  {step.description}
                  {isCompleted && (
                    <div className="step-tooltip-status step-tooltip-status--completed">
                      ✓ Completed{canViewResults ? ' — click name to view' : ''}
                    </div>
                  )}
                  {isFailed && (
                    <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.8, color: '#ef4444', fontWeight: '600' }}>
                      ✗ Failed — click refresh to retry
                    </div>
                  )}
                  {isBlocked && (
                    <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.8, color: '#f59e0b', fontWeight: '600' }}>
                      Blocked — resolve prerequisites first
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}
